import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["as", "buttonType", "children", "fluid", "icon", "iconPosition", "iconSize", "size"];
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ButtonMixin } from '../../mixins/components/ButtonMixin';
import { ButtonIconMixin } from '../../mixins/components/ButtonIconMixin';
import { BUTTON_TYPES, BUTTON_SIZES } from '../../constants/BUTTON';
import { ButtonGroupContext } from '../../js/ButtonGroupContext';
import CustomPropTypes from '../../util/custom-prop-types';
export var StyledButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "StyledButton",
  componentId: "c11n-8-64-0__sc-wpcbcc-0"
})(["", ""], ButtonMixin);
export var StyledButtonIcon = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledButtonIcon",
  componentId: "c11n-8-64-0__sc-wpcbcc-1"
})(["", ""], ButtonIconMixin);
/**
 * See also [`ButtonGroup`](#/Button%20components?id=buttongroup),
 * [`TextButton`](#/Button%20components?id=textbutton),
 * [`ToggleButton`](#/Button%20components?id=togglebutton)
 */

var Button = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var as = _ref.as,
      buttonType = _ref.buttonType,
      children = _ref.children,
      fluid = _ref.fluid,
      icon = _ref.icon,
      iconPosition = _ref.iconPosition,
      iconSize = _ref.iconSize,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, _excluded);

  var context = React.useContext(ButtonGroupContext);
  var buttonProps = Object.assign({
    as: as,
    buttonType: context.buttonType || buttonType,
    fluid: context.fluid || fluid,
    size: context.size || size,
    ref: ref
  }, rest); // Inputs do not allow icon children

  if (as === 'input') {
    return /*#__PURE__*/React.createElement(StyledButton, buttonProps);
  }

  var startIcon;

  if (iconPosition === 'start' && icon) {
    startIcon = /*#__PURE__*/React.createElement(StyledButtonIcon, {
      iconPosition: iconPosition,
      iconSize: iconSize
    }, icon);
  }

  var endIcon;

  if (iconPosition === 'end' && icon) {
    endIcon = /*#__PURE__*/React.createElement(StyledButtonIcon, {
      iconPosition: iconPosition,
      iconSize: iconSize
    }, icon);
  }

  return /*#__PURE__*/React.createElement(StyledButton, buttonProps, startIcon, children, endIcon);
});
Button.displayName = 'Button';
Button.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node,

  /**
   * Set the button as disabled.
   */
  disabled: PropTypes.bool,

  /**
   * A fluid button will take up all horizontal space.
   * Can be inherited from a parent ButtonGroup.
   *
   * Supports [inline media query objects](#/Media%20queries)
   */
  fluid: CustomPropTypes.inlineMediaQuery(PropTypes.bool),

  /**
   * Add an [`Icon`](#/More%20components?id=icon) to the button.
   */
  icon: PropTypes.node,

  /**
   * Change the position of the `icon`.
   */
  iconPosition: PropTypes.oneOf(['start', 'end']),

  /**
   * This is not an exposed API property. It is only used internally.
   *
   * @ignore
   */
  iconSize: PropTypes.string,

  /**
   * Inverse colors for use on dark or colored backgrounds.
   */
  inverse: PropTypes.bool,

  /**
   * The size of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  size: PropTypes.oneOf(Object.keys(BUTTON_SIZES)),

  /**
   * The type of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  buttonType: PropTypes.oneOf(Object.keys(BUTTON_TYPES)),

  /**
   * Use the styled-components
   * [polymorphic "as" prop](https://www.styled-components.com/docs/api#as-polymorphic-prop)
   * to change the HTML tag of the component.
   *
   * This can be used to apply `Button` styling to HTML anchors, inputs, and custom components.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  as: PropTypes.any
} : {};
Button.defaultProps = {
  children: undefined,
  disabled: undefined,
  fluid: false,
  icon: undefined,
  iconPosition: 'start',
  iconSize: 'sm',
  inverse: false,
  size: BUTTON_SIZES.md,
  buttonType: BUTTON_TYPES.secondary,
  as: 'button'
};
/** @component */

export default Button;